import { useEffect, useRef, useState } from "react"
import SchemaFieldRow from "./SchemaFieldRow"
import SchemaPreview from "./SchemaPreview"
import {
    SCHEMA_LIMITS,
    createEmptyField,
    fieldMatchesQuery,
} from "./schemaConstants"

export default function SchemaBuilder({ schema, onChange, disabled, errors = {} }) {
    const [showPreview, setShowPreview] = useState(false)
    const [searchQuery, setSearchQuery] = useState("")
    const [dragIndex, setDragIndex] = useState(null)
    const [dragOverIndex, setDragOverIndex] = useState(null)
    const previewRef = useRef(null)

    useEffect(() => {
        if (showPreview && previewRef.current) {
            previewRef.current.scrollIntoView({ behavior: "smooth", block: "start" })
        }
    }, [showPreview])

    const fields = schema.fields || []

    const addField = () => {
        if (fields.length >= SCHEMA_LIMITS.MAX_FIELDS) return
        onChange({
            ...schema,
            fields: [...fields, createEmptyField()],
        })
    }

    const removeField = (fieldId) => {
        onChange({
            ...schema,
            fields: fields.filter((f) => f.id !== fieldId),
        })
    }

    const updateField = (fieldId, updates) => {
        onChange({
            ...schema,
            fields: fields.map((f) =>
                f.id === fieldId ? { ...f, ...updates } : f
            ),
        })
    }

    const q = searchQuery.trim().toLowerCase()
    const isSearching = q.length > 0
    const visibleFields = isSearching
        ? fields.filter((f) => fieldMatchesQuery(f, q))
        : fields

    const handleDragStart = (index) => {
        setDragIndex(index)
    }

    const handleDragOver = (index) => {
        if (dragIndex === null || dragIndex === index) return
        setDragOverIndex(index)
    }

    const handleDragEnd = () => {
        if (
            dragIndex !== null &&
            dragOverIndex !== null &&
            dragIndex !== dragOverIndex
        ) {
            const next = [...fields]
            const [moved] = next.splice(dragIndex, 1)
            next.splice(dragOverIndex, 0, moved)
            onChange({ ...schema, fields: next })
        }
        setDragIndex(null)
        setDragOverIndex(null)
    }

    return (
        <div className="mt-4 border border-gray-200 rounded-2xl p-5 bg-gray-50">
            {/* Header */}
            <div className="flex items-center justify-between mb-4">
                <h3 className="text-md font-semibold text-gray-700">
                    Structured Output Schema
                </h3>
                <button
                    type="button"
                    onClick={() => setShowPreview(!showPreview)}
                    className="text-sm text-indigo-600 hover:text-indigo-800 font-medium"
                >
                    {showPreview ? "Hide Preview" : "Show Preview"}
                </button>
            </div>

            {/* Schema Name */}
            <div className="mb-4">
                <label className="text-sm text-gray-600 mb-1 block">
                    Schema Name{" "}
                    <span className="text-gray-400 text-xs">(optional)</span>
                </label>
                <div className="relative">
                    <input
                        type="text"
                        value={schema.schema_name}
                        onChange={(e) =>
                            onChange({ ...schema, schema_name: e.target.value })
                        }
                        maxLength={SCHEMA_LIMITS.SCHEMA_NAME_MAX}
                        placeholder='e.g., "Risk Assessment"'
                        disabled={disabled}
                        className="w-full border border-gray-300 rounded-lg px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-black"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400">
                        {schema.schema_name.length}/{SCHEMA_LIMITS.SCHEMA_NAME_MAX}
                    </span>
                </div>
            </div>

            {/* Search */}
            {fields.length > 0 && (
                <div className="mb-3">
                    <input
                        type="text"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder="Search fields by name or description..."
                        disabled={disabled}
                        className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-black"
                    />
                </div>
            )}

            {/* Fields */}
            {fields.length === 0 ? (
                <p className="text-sm text-gray-400 py-6 text-center">
                    No fields yet. Add one to define the output schema.
                </p>
            ) : visibleFields.length === 0 ? (
                <p className="text-sm text-gray-400 py-6 text-center">
                    No fields match "{searchQuery}".
                </p>
            ) : (
                visibleFields.map((field) => {
                    const originalIndex = fields.indexOf(field)
                    return (
                        <SchemaFieldRow
                            key={field.id}
                            field={field}
                            index={originalIndex}
                            onChange={updateField}
                            onRemove={removeField}
                            isDisabled={disabled}
                            errors={errors}
                            depth={1}
                            isSortable={!isSearching}
                            isDragging={dragIndex === originalIndex}
                            isDragOver={dragOverIndex === originalIndex}
                            onDragStart={handleDragStart}
                            onDragOver={handleDragOver}
                            onDragEnd={handleDragEnd}
                        />
                    )
                })
            )}

            {/* Add Field + Counter */}
            <div className="flex items-center justify-between mt-3">
                <button
                    type="button"
                    onClick={addField}
                    disabled={disabled || fields.length >= SCHEMA_LIMITS.MAX_FIELDS}
                    className="inline-flex items-center gap-1.5 text-sm font-medium text-indigo-600 hover:text-indigo-800 disabled:text-gray-400 disabled:cursor-not-allowed"
                >
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-4 h-4">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                    </svg>
                    Add Field
                </button>
                <span className="text-xs text-gray-400">
                    {fields.length}/{SCHEMA_LIMITS.MAX_FIELDS} fields
                </span>
            </div>

            {/* Preview */}
            {showPreview && fields.length > 0 && (
                <div ref={previewRef} className="scroll-mt-4">
                    <SchemaPreview fields={fields} />
                </div>
            )}
        </div>
    )
}
